/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Tab, Tabs } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { TabMenu } from 'models/md/MdIves';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { tabs } from 'components/layouts/Tab';
import IvesAtc from './atcMgt/IvesAtc';
import DtlIsptMatr from './atcMgt/DtlIsptMatr';
import { MdArticleCondition } from 'models/md/MdArticle';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { hasRole } from 'utils/SessionUtil';
import { findMdIvesAtcRegList } from 'apis/md/MdIvesAtcRegt';
import { Code } from 'models/common/CommonCode';

/**
 * @desc 투자비관리 > 기준정보 > 투자비 산출 항목 관리
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.27   jshwang12       신규 생성
 */
const MdIvesAtcMgtPage = () => {
  const atcRef = useRef<any>();
  const dtlRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();

  const initCondition: MdArticleCondition = {
    ivesAtcIds: [], // 투자항목ID 목록
    prdnProcCds: [], // 생산공정코드 목록
    eqclIds: [], // 설비군ID 목록
    cellDivsCds: [], // 셀구분코드 목록
    dataInsUserId: '', // 데이터입력사용자ID
    dtlIsptId: '', // 상세점검ID
  };
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<MdArticleCondition>({ ...initCondition });
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [isOpenUserPopup, setIsOpenUserPopup] = useState(false);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const tabMenus: TabMenu[] = [
    { id: 'IVES', name: String(t('md.label.투자항목', '투자항목')) },
    { id: 'DETAIL', name: String(t('md.label.상세 점검사항', '상세 점검사항')) },
  ];

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
    getCommonCodes();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [currentTabIndex]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNamesCondition({
        cmnGrCd: 'PRDN_PROC_CD',
        optValCtn7: 'Y', // 형개발투자비 산출
      }),
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_ID',
        optValCtn2: 'Y', // 투자비항목대상
      }),
      getCommonCodeNames('MD_CELL_DIVS_CD'),
      findMdIvesAtcRegList({}),
    ]).then(([prdnProcCd, mdEqclId, mdCellDivsCd, mdAtcList]) => {
      setCode({
        prdnProcCd: prdnProcCd,
        mdEqclId: mdEqclId,
        mdCellDivsCd: mdCellDivsCd,
        mdAtcList: (mdAtcList || []).reduce(
          (acc, cur) => [
            ...acc,
            {
              cmnCd: cur.ivesAtcId,
              cmnCdNm: cur.ivesAtcNm,
            },
          ],
          [] as Code[]
        ),
      });
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    if (currentTabIndex === 0) {
      atcRef.current?.search(condition);
    } else {
      dtlRef.current?.search(condition);
    }
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('md.label.공정', '공정'))}</label>
                <MultiComboBox
                  id="prdnProcCd"
                  options={code?.prdnProcCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.prdnProcCds || []}
                  onChange={(value) => {
                    handleOnChange('prdnProcCds', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.설비군', '설비군'))}</label>
                <MultiComboBox
                  id="prdnProcCd"
                  options={code?.mdEqclId || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.eqclIds || []}
                  onChange={(value) => {
                    handleOnChange('eqclIds', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.셀 구분', '셀 구분')}</label>
                <MultiComboBox
                  id="cellDivsCd"
                  options={code?.mdCellDivsCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.cellDivsCds || []}
                  onChange={(value) => {
                    handleOnChange('cellDivsCds', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('md.label.투자항목', '투자항목')}</label>
                <MultiComboBox
                  id="ivesAtcIds"
                  options={code?.mdAtcList || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.ivesAtcIds || []}
                  onChange={(value) => {
                    handleOnChange('ivesAtcIds', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.단가기준', '단가기준')}</label>
                <CustomInputWithSearch
                  name="uprcDcnCtn"
                  value={condition.uprcDcnCtn}
                  placeholder={String(
                    t('md.label.단가기준을 입력해 주세요.', '단가기준을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange('uprcDcnCtn', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.등록자', '등록자')}</label>
                <CustomInputWithSearch
                  name="dataInsUserId"
                  className="find"
                  placeholder={String(
                    t('com.label.등록자를 입력해 주세요.', '등록자를 입력해 주세요.')
                  )}
                  value={condition.dataInsUserId}
                  onSearchClick={(e) => setIsOpenUserPopup(true)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => handleSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <div css={tabs.wrap} className="mt-6">
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`td-ives-cmpu-tab-${idx}`}
              aria-controls={`td-ives-cmpu-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
      </div>
      <TabPanel value={currentTabIndex} index={0}>
        <IvesAtc ref={atcRef} isReadOnly={!hasAuth} />
      </TabPanel>
      <TabPanel value={currentTabIndex} index={1}>
        <DtlIsptMatr ref={dtlRef} isReadOnly={!hasAuth} />
      </TabPanel>

      {isOpenUserPopup && (
        <UserMultiModal
          open={isOpenUserPopup}
          close={() => setIsOpenUserPopup(false)}
          title={t('com.label.등록자 조회', '등록자 조회')}
          condition={{
            roles: ['MD_IVES_MANAGER'], // 투자 관리자
          }}
          defaultUserId={''}
          singleSelect={true}
          onCallback={(callbackData: any) => {
            if (callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return false;
            }
            setCondition((prev) => ({
              ...prev,
              dataInsUserId: callbackData.userId,
              dataInsUserNm: callbackData.empNm,
            }));
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`setup-schedule-tabpanel-${index}`}
      className="mb-5"
    >
      <div>{children}</div>
    </div>
  );
}

export default MdIvesAtcMgtPage;
